import { useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { PiWarningBold } from "react-icons/pi";
import { MdClose } from "react-icons/md";
import { formatTimestamp } from "../utils";
import { AccountContext } from "../contexts/AccountContexts";
import { ContentContext } from "../contexts/ContentContext";
import { requestCreateManualWarning, requestEmployeeWarnings } from "../requests/warning_requests";

export const warningTypes = ["response_delay", "response_escalation", "auto_reply_close", "manual", "keyword"];
export const warningStatuses = ["NEW", "ANSWERED", "REVIEWED", "ACTIONED"];

export const warningTypeStyle = {
    response_delay: "bg-orange-500",
    response_escalation: "bg-red-800",
    auto_reply_close: "bg-purple-600",
    manual: "bg-red-600",
    keyword: "bg-rose-700",
};

export const warningStatusStyle = {
    NEW: "badge-error",
    REVIEWED: "badge-warning",
    ACTIONED: "badge-success",
    ANSWERED: "badge-info",
};

export const WarningIcon = ({ size = 14 }) => <PiWarningBold size={size} />;

export const WarningTypeBadge = ({ type }) => {
    const { t } = useTranslation("common");
    return (
        <span className={`badge border-none text-white whitespace-nowrap ${warningTypeStyle[type] || "bg-gray-500"}`}>
            {t(`warning.type.${type}`)}
        </span>
    );
};

// Tickets opened from a warning leave their notifications unread (see EditTicket)
export const ticketLink = (code) => `/admin/${code}/edit?from=warning`;

// "2009.5" -> "2010", "3.25" -> "3.3": whole hours once the number is large enough.
const formatHours = (hours) => (hours >= 10 ? Math.round(hours) : Math.round(hours * 10) / 10);

// One-line description of a warning, used in notifications and lists.
export const warningSummary = (t, warning, role = "admin") => {
    const d = warning.details || {};
    const ticket = warning.ticket_created_at;
    // Notifications carry the kind in `warning_type` (their own `type` is "warning").
    switch (warning.warning_type || warning.type) {
        case "response_delay":
            return role === "employee"
                ? t("warning.text.response_delay_employee", { ticket, hours: formatHours(d.waiting_hours) })
                : t("warning.text.response_delay", { ticket, hours: formatHours(d.waiting_hours), user: d.employee_name });
        case "response_escalation":
            // the admins' text tells them to reassign the ticket; the employee only sees their own delay
            if (role === "employee") return t("warning.text.response_escalation_employee", { ticket, hours: formatHours(d.waiting_hours), level: d.level_hours });
            return t(d.final_level ? "warning.text.response_escalation_final" : "warning.text.response_escalation", { ticket, hours: formatHours(d.waiting_hours), user: d.employee_name, level: d.level_hours });
        case "response_reminder":
            return t("warning.text.response_reminder", { ticket, hours: formatHours(d.waiting_hours), remaining: formatHours(d.remaining_hours) });
        case "auto_reply_close": {
            const [a, b] = d.tickets || [];
            return t(role === "employee" ? "warning.text.auto_reply_close_employee" : "warning.text.auto_reply_close", { first: a?.created_at, second: b?.created_at, player: d.player_id });
        }
        case "manual":
            return role === "employee"
                ? t("warning.text.manual_employee", { ticket, user: d.created_by_name, reason: d.reason })
                : t("warning.text.manual", { ticket, user: d.employee_name, reason: d.reason });
        case "keyword":
            return t("warning.text.keyword", { ticket, keyword: d.keyword });
        default:
            return "";
    }
};

// Short quote of the player's message for keyword notifications ("" for other types).
export const warningExcerpt = (notification, max = 120) => {
    const excerpt = notification.warning_type === "keyword" ? notification.details?.excerpt : null;
    if (!excerpt) return "";
    return excerpt.length > max ? excerpt.slice(0, max) + "…" : excerpt;
};

// Notification menu line: the summary, plus the quoted message for keyword warnings.
export const WarningNotificationText = ({ notification }) => {
    const { t } = useTranslation("common");
    const excerpt = warningExcerpt(notification);
    return (
        <span>
            {warningSummary(t, notification, notification.details?.role)}
            {excerpt && <span className="block text-xs italic opacity-80 whitespace-pre-wrap break-words">"{excerpt}"</span>}
        </span>
    );
};

// Extra lines shown under a warning in the warnings page.
export const WarningDetails = ({ warning }) => {
    const { t } = useTranslation("common");
    const d = warning.details || {};
    if (warning.type === "auto_reply_close") {
        return (
            <div className="flex flex-col gap-1">
                {(d.tickets || []).map((tk) => (
                    <div key={tk.id} className="text-xs">
                        <NavLink className="text-primary dark:text-blue-400 underline" to={ticketLink(tk.code)}>#{tk.created_at}</NavLink>
                        {" · "}{t(`warning.ticket_status.${tk.status}`, tk.status)}{" · "}{tk.by_name}{" · "}
                        {t(tk.kind === "excuse" ? "warning.reply_kind.excuse" : "warning.reply_kind.quick_reply")}: <i>{tk.reply}</i>
                    </div>
                ))}
            </div>
        );
    }
    if (warning.type === "keyword" || warning.type === "manual") {
        return (
            <div className="text-xs flex flex-col gap-1">
                {d.excerpt && <span className="italic opacity-80 line-clamp-3 whitespace-pre-wrap">"{d.excerpt}"</span>}
                {warning.type === "keyword" && d.assigned_name && <span>{t("warning.assigned_to", { user: d.assigned_name })}</span>}
                {warning.type === "manual" && <span>{t("warning.given_by", { user: d.created_by_name })}</span>}
            </div>
        );
    }
    if (warning.type === "response_delay" || warning.type === "response_escalation") {
        return (
            <div className="text-xs flex flex-col gap-1">
                <span>{t("warning.waiting_since", { time: formatTimestamp(d.waiting_since * 1000), limit: warning.type === "response_escalation" ? d.level_hours : d.limit_hours })}</span>
                {d.answered_at && (
                    <span className="text-sky-700 dark:text-sky-300">
                        {t(d.answer_kind === "closed" ? "warning.answered_closed" : "warning.answered", { hours: formatHours(d.answered_after_hours), user: d.answered_by_name })}
                    </span>
                )}
            </div>
        );
    }
    return null;
};

export const ManualWarningModal = ({ message, employeeName, onClose }) => {
    const { t } = useTranslation("common");
    const { openErrorModal } = useContext(ContentContext);
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const [done, setDone] = useState(false);

    const handleSubmit = () => {
        setSaving(true);
        requestCreateManualWarning({ ticketId: message.ticket_id, messageId: message.id, reason: reason })
            .then(() => setDone(true))
            .catch((error) => {
                console.error(error.status, error.message);
                openErrorModal(error);
            })
            .finally(() => setSaving(false));
    };

    // Portal: the chat bubble around the caller must not clip or move the dialog.
    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" onMouseDown={(e) => e.stopPropagation()}>
            <div className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded-lg shadow-xl w-[92vw] max-w-lg p-5 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                    <h3 className="font-semibold flex items-center gap-2"><WarningIcon size={18} /> {t("warning.manual_title", { user: employeeName })}</h3>
                    <button className="btn btn-sm btn-ghost" onClick={onClose}><MdClose /></button>
                </div>
                <div className="text-sm italic border-l-4 border-gray-300 dark:border-gray-600 pl-2 max-h-32 overflow-y-auto whitespace-pre-wrap">
                    {message.message}
                </div>
                {done ? (
                    <>
                        <p className="text-sm text-green-700 dark:text-green-400">{t("warning.manual_sent")}</p>
                        <button className="btn btn-sm self-end" onClick={onClose}>{t("warning.close")}</button>
                    </>
                ) : (
                    <>
                        <label className="text-sm font-medium">{t("warning.reason")}</label>
                        <textarea
                            className="textarea textarea-bordered w-full h-28 bg-inherit"
                            value={reason}
                            maxLength={1000}
                            placeholder={t("warning.reason_placeholder")}
                            onChange={(e) => setReason(e.target.value)}
                            autoFocus
                        />
                        <div className="flex justify-end gap-2">
                            <button className="btn btn-sm btn-ghost" onClick={onClose}>{t("warning.cancel")}</button>
                            <button className="btn btn-sm bg-red-600 hover:bg-red-700 text-white border-none" disabled={!reason.trim() || saving} onClick={handleSubmit}>
                                {t("warning.give")}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>,
        document.body
    );
};

// Warning history of one employee (user detail page).
export const EmployeeWarningsList = ({ userId }) => {
    const { t } = useTranslation("common");
    const [warnings, setWarnings] = useState(null);

    useEffect(() => {
        if (!userId) return;
        requestEmployeeWarnings({ userId })
            .then((data) => setWarnings(data.warnings))
            .catch((error) => {
                console.error(error.status, error.message);
                setWarnings([]);
            });
    }, [userId]);

    if (warnings === null) return null;

    return (
        <div className="flex flex-col gap-2 w-full">
            <h2 className="text-lg font-semibold flex items-center gap-2"><WarningIcon size={18} /> {t("warning.history_title", { count: warnings.length })}</h2>
            {warnings.length === 0 && <span className="text-sm italic opacity-70">{t("warning.history_empty")}</span>}
            {warnings.map((w) => (
                <div key={w.id} className="flex flex-col gap-1 border border-gray-300 dark:border-gray-600 rounded-md p-2 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                        <WarningTypeBadge type={w.type} />
                        <span className={`badge badge-outline ${warningStatusStyle[w.status]}`}>{t(`warning.status.${w.status}`)}</span>
                        <span className="opacity-70">{formatTimestamp(w.timestamp * 1000)}</span>
                        {w.ticket_code && (
                            <NavLink className="text-primary dark:text-blue-400 underline" to={ticketLink(w.ticket_code)}>#{w.ticket_created_at}</NavLink>
                        )}
                    </div>
                    <span>{warningSummary(t, w)}</span>
                    <WarningDetails warning={w} />
                </div>
            ))}
        </div>
    );
};

export const useDevName = () => {
    const { devs } = useContext(AccountContext);
    return (id) => (id && devs && devs[id] ? devs[id].name : id ? id.slice(0, 8) : "-");
};
